const ALLOWED_OUTCOMES = new Set([
  'satisfactory',
  'needs_practice',
]);

const TRAINING_CRITERION_TAG = /^\[(SCHOOL PLAN|SCHOOL TK MILESTONE|REGULATORY RECORD|CAA [^\]]+)\]\s*/i;
const CAA_SOURCE_MAPPING = /\s*\[CAA source mapping:\s*([^\]]+)\]\s*$/i;

export function presentTrainingCriterion(description) {
  let text = String(description || '');
  const tags = [];
  let sourceMapping = '';
  const mappingMatch = text.match(CAA_SOURCE_MAPPING);

  if (mappingMatch) {
    sourceMapping = mappingMatch[1];
    text = text.slice(0, mappingMatch.index).trimEnd();
  }

  let tagMatch = text.match(TRAINING_CRITERION_TAG);
  while (tagMatch) {
    tags.push(`[${tagMatch[1]}]`);
    text = text.slice(tagMatch[0].length);
    tagMatch = text.match(TRAINING_CRITERION_TAG);
  }

  return { text, tags, sourceMapping };
}

export function getTrainingExerciseReferenceDetails(items = []) {
  const tags = new Set();
  const sourceMappings = new Set();

  items.forEach((item) => {
    const presentation = presentTrainingCriterion(item.description);
    presentation.tags.forEach((tag) => tags.add(tag));
    if (presentation.sourceMapping) sourceMappings.add(presentation.sourceMapping);
  });

  return {
    tags: [...tags],
    sourceMappings: [...sourceMappings],
  };
}

export function getTrainingAssessmentHistory(briefingData) {
  if (
    !briefingData ||
    typeof briefingData !== 'object' ||
    !Array.isArray(briefingData.trainingAssessments)
  ) {
    return [];
  }

  return briefingData.trainingAssessments.filter(
    (assessment) =>
      assessment &&
      typeof assessment === 'object' &&
      !Array.isArray(assessment)
  );
}

export async function fetchSyllabusExercises(
  supabase,
  courseEnrolmentId,
  organisationId
) {
  if (!courseEnrolmentId) {
    return {
      data: { syllabusVersionId: null, exercises: [] },
      error: null,
    };
  }
  if (!organisationId) {
    return {
      data: null,
      error: new Error('Could not identify the booking organisation.'),
    };
  }

  const {
    data: enrolment,
    error: enrolmentError,
  } = await supabase
    .from('course_enrolments')
    .select('id, syllabus_version_id')
    .eq('id', courseEnrolmentId)
    .eq('organisation_id', organisationId)
    .eq('status', 'active')
    .maybeSingle();

  if (enrolmentError) {
    return { data: null, error: enrolmentError };
  }

  if (!enrolment?.syllabus_version_id) {
    return {
      data: null,
      error: new Error('The active course enrolment or its syllabus is unavailable.'),
    };
  }

  const {
    data: syllabusVersion,
    error: syllabusVersionError,
  } = await supabase
    .from('training_syllabus_versions')
    .select('id, version_code, source_reference')
    .eq('id', enrolment.syllabus_version_id)
    .eq('organisation_id', organisationId)
    .maybeSingle();

  if (syllabusVersionError) {
    return { data: null, error: syllabusVersionError };
  }
  if (!syllabusVersion) {
    return {
      data: null,
      error: new Error('The assigned syllabus version is unavailable.'),
    };
  }

  const {
    data: exercises,
    error: exercisesError,
  } = await supabase
    .from('training_exercises')
    .select('id, exercise_reference, title, display_order')
    .eq('organisation_id', organisationId)
    .eq('syllabus_version_id', enrolment.syllabus_version_id)
    .order('display_order', { ascending: true });

  if (exercisesError) {
    return { data: null, error: exercisesError };
  }

  const exerciseRows = exercises || [];
  if (exerciseRows.length === 0) {
    return {
      data: {
        syllabusVersionId: enrolment.syllabus_version_id,
        syllabusVersionCode: syllabusVersion.version_code,
        sourceReference: syllabusVersion.source_reference || '',
        exercises: [],
      },
      error: null,
    };
  }

  const {
    data: items,
    error: itemsError,
  } = await supabase
    .from('training_exercise_items')
    .select('id, exercise_id, item_reference, description, display_order')
    .eq('organisation_id', organisationId)
    .in('exercise_id', exerciseRows.map((exercise) => exercise.id))
    .order('display_order', { ascending: true });

  if (itemsError) {
    return { data: null, error: itemsError };
  }

  return {
    data: {
      syllabusVersionId: enrolment.syllabus_version_id,
      syllabusVersionCode: syllabusVersion.version_code,
      sourceReference: syllabusVersion.source_reference || '',
      exercises: exerciseRows.map((exercise) => ({
        ...exercise,
        items: (items || []).filter(
          (item) => String(item.exercise_id) === String(exercise.id)
        ),
      })),
    },
    error: null,
  };
}

export async function saveTrainingLessonAssessment({
  supabase,
  booking,
  instructorProfile,
  exerciseAssessments,
  overallOutcome,
  lessonNotes,
  nextLessonFocus,
  correctionOfAssessedAt,
  correctionReason,
  assessedAt = new Date().toISOString(),
}) {
  if (
    !booking?.id ||
    booking.booking_type !== 'training' ||
    !booking.instructor_user_id ||
    !instructorProfile?.id ||
    String(booking.instructor_user_id) !== String(instructorProfile.id)
  ) {
    return {
      ok: false,
      error: new Error('Only the instructor assigned to this training booking can assess the lesson.'),
    };
  }

  if (!booking.course_enrolment_id) {
    return {
      ok: false,
      error: new Error('Link this training booking to an active course enrolment before recording an assessment.'),
    };
  }

  const suppliedAssessments = exerciseAssessments || [];
  if (!Array.isArray(suppliedAssessments)) {
    return {
      ok: false,
      error: new Error('Exercise assessments must be provided as a list.'),
    };
  }

  const validAssessments = suppliedAssessments.filter(
    (assessment) =>
      assessment &&
      assessment.exerciseId &&
      assessment.itemId &&
      ALLOWED_OUTCOMES.has(assessment.outcome)
  );
  if (validAssessments.length !== suppliedAssessments.length) {
    return {
      ok: false,
      error: new Error('One or more exercise outcomes are invalid.'),
    };
  }
  if (!ALLOWED_OUTCOMES.has(overallOutcome)) {
    return {
      ok: false,
      error: new Error('Select a valid overall lesson outcome.'),
    };
  }
  const trimmedCorrectionReason = String(correctionReason || '').trim();
  if (correctionOfAssessedAt) {
    const history = getTrainingAssessmentHistory(booking.briefing_data);
    const correctionTarget = history.find(
      (item) => item.assessedAt === correctionOfAssessedAt
    );
    const targetAlreadyCorrected = history.some(
      (item) => item.correctionOfAssessedAt === correctionOfAssessedAt
    );
    if (!correctionTarget || targetAlreadyCorrected || !trimmedCorrectionReason) {
      return {
        ok: false,
        error: new Error('Select an uncorrected assessment and provide a correction reason.'),
      };
    }
  } else if (trimmedCorrectionReason) {
    return {
      ok: false,
      error: new Error('A correction reason requires a correction target.'),
    };
  }
  const trimmedLessonNotes = String(lessonNotes || '').trim();
  const trimmedNextLessonFocus = String(nextLessonFocus || '').trim();

  if (
    validAssessments.length === 0 &&
    !trimmedLessonNotes &&
    !trimmedNextLessonFocus
  ) {
    return {
      ok: false,
      error: new Error('Add an exercise assessment, lesson notes or next-lesson focus before saving.'),
    };
  }

  const assessment = {
    assessedAt,
    assessedByUserId: instructorProfile.id,
    assessedByName: String(instructorProfile.full_name || '').trim(),
    courseEnrolmentId: booking.course_enrolment_id || null,
    syllabusVersionId: booking.syllabusVersionId || null,
    exerciseAssessments: validAssessments,
    overallOutcome,
    lessonNotes: trimmedLessonNotes,
    nextLessonFocus: trimmedNextLessonFocus,
    ...(correctionOfAssessedAt
      ? {
        correctionOfAssessedAt,
        correctionReason: trimmedCorrectionReason,
      }
      : {}),
  };
  const { data: updatedBriefing, error: updateError } = await supabase.rpc(
    'training_append_lesson_assessment',
    { p_booking_id: booking.id, p_assessment: assessment }
  );

  if (updateError) {
    return { ok: false, error: updateError };
  }

  if (!updatedBriefing || typeof updatedBriefing !== 'object') {
    return {
      ok: false,
      error: new Error('The database did not authorize saving this lesson assessment.'),
    };
  }

  return {
    ok: true,
    assessment,
    briefingData: updatedBriefing,
  };
}
